import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/auth/auth';
import { Loader2 } from 'lucide-react';

interface OnboardingRouteProps {
  children: ReactNode;
}

/**
 * OnboardingRoute Component
 *
 * Route wrapper for the onboarding page that handles auth state appropriately:
 * - BOOTING → Loading spinner
 * - UNAUTHENTICATED → Redirect to login
 * - GATED → Render children (allow onboarding flow)
 * - AUTHENTICATED → Redirect to app (fully authenticated)
 * - ERROR → Show error state
 *
 * IMPORTANT: We do NOT auto-redirect when gatingReason changes from
 * ONBOARDING_REQUIRED to PENDING_APPROVAL. This would cause oscillation
 * because the component unmounts/remounts during state transitions.
 * Instead, the user navigates manually via "Go to Dashboard" button.
 */
export function OnboardingRoute({ children }: OnboardingRouteProps) {
  const navigate = useNavigate();
  const { authState } = useAuth();

  // Handle redirects based on auth state
  // Note: We only redirect for UNAUTHENTICATED and AUTHENTICATED states.
  // GATED users (regardless of gatingReason) stay on onboarding to complete
  // the flow and manually navigate when ready.
  useEffect(() => {
    if (authState === 'UNAUTHENTICATED') {
      navigate('/login', { replace: true });
    } else if (authState === 'AUTHENTICATED') {
      // Fully authenticated (active account) - redirect to app
      navigate('/app/livespeak', { replace: true });
    }
  }, [authState, navigate]);

  // Loading state
  if (authState === 'BOOTING') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (authState === 'ERROR') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center space-y-4">
          <p className="text-destructive">An error occurred. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            className="text-primary underline"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // Unauthenticated - show nothing while redirect happens
  if (authState === 'UNAUTHENTICATED') {
    return null;
  }

  // Already authenticated - show nothing while redirect happens
  if (authState === 'AUTHENTICATED') {
    return null;
  }

  // GATED (any reason) - render onboarding
  // This allows users to complete onboarding and see the completion screen
  // before manually navigating to dashboard via "Go to Dashboard" button
  return <>{children}</>;
}

export default OnboardingRoute;
